/**
 * Location Selector Component
 *
 * Selects dependientes para Departamento y Ciudad/Municipio de Honduras.
 * Cuando se selecciona un departamento, se filtran los municipios correspondientes.
 */

import { useTranslation } from "react-i18next";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { getDepartamentos, getMunicipios } from "@/data/honduras-locations";

interface LocationSelectorProps {
  departamento: string;
  ciudad: string;
  onDepartamentoChange: (value: string) => void;
  onCiudadChange: (value: string) => void;
  disabled?: boolean;
  errors?: {
    departamento?: string;
    ciudad?: string;
  };
}

// Cache departamentos since they never change
const DEPARTAMENTOS = getDepartamentos();

export function LocationSelector({
  departamento,
  ciudad,
  onDepartamentoChange,
  onCiudadChange,
  disabled = false,
  errors,
}: LocationSelectorProps) {
  const { t } = useTranslation();

  // Compute municipios based on departamento
  const municipios = departamento ? getMunicipios(departamento) : [];

  const handleDepartamentoChange = (value: string) => {
    onDepartamentoChange(value);
    // Clear ciudad when user manually changes departamento
    onCiudadChange("");
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>{t("pacientes.fields.department")}</Label>
        <Select value={departamento} onValueChange={handleDepartamentoChange} disabled={disabled}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("pacientes.fields.selectDepartment")} />
          </SelectTrigger>
          <SelectContent>
            {DEPARTAMENTOS.map((dept) => (
              <SelectItem key={dept} value={dept}>
                {dept}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors?.departamento && (
          <p className="text-sm text-destructive">{t(errors.departamento)}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t("pacientes.fields.city")}</Label>
        <Select value={ciudad} onValueChange={onCiudadChange} disabled={disabled || !departamento}>
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={
                departamento
                  ? t("pacientes.fields.selectCity")
                  : t("pacientes.fields.selectDepartmentFirst")
              }
            />
          </SelectTrigger>
          <SelectContent>
            {municipios.map((mun) => (
              <SelectItem key={mun} value={mun}>
                {mun}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors?.ciudad && <p className="text-sm text-destructive">{t(errors.ciudad)}</p>}
      </div>
    </div>
  );
}
